import { StatsApiResponse } from "../../../models/stats-response";
import { UserAccount } from "../../../models/custom-models/user-account";
import { CustomDropdownOption } from "../../../modules/shared/Models/shared-data-models.model";
import { StatsRequest } from "../../../models/stats-request";
import { EntityInfo } from "../../../models/custom-models/entity-info";
import {OrgBUProjectTeamStructure} from "../../../models/custom-models/org-buproject-team-structure";
import {AiInsDetectionResponse} from "../../Shared/voice-instructions/Models/ai-model.model";

export class Dashboard {}

export class GetUserProgressRequest {
    orgId: number | null;
    teamId: number | null;
    fromDate: string;
    toDate: string;
}

export class ProgressData {
    sprintProgress: SprintProgress[];
    sprintTotalHours: number;
    consumedSprintHours: number;
    taskProgress: TaskProgress[];
    taskTotalHours: number;
    consumedTaskHours: number;
    meetingProgress: MeetingProgress[];
    meetingTotalHours: number;
    consumedMeetingHours: number;

    constructor(data: Partial<ProgressData>) {
        Object.assign(this, data);
    }

    get totalProgressPercentage(): number {
        const totalHours = this.sprintTotalHours + this.taskTotalHours + this.meetingTotalHours;
        const consumedHours = this.consumedSprintHours + this.consumedTaskHours + this.consumedMeetingHours;
        if (totalHours <= 0) {
            return 0;
        }
        const progressPercentage = (consumedHours / totalHours) * 100;
        return Math.round(Math.max(progressPercentage, 0) * 100) / 100;
    }

    get sprintProgressPercentage(): number {
        if (this.sprintTotalHours <= 0) {
            return 0;
        }
        const progressPercentage = (this.consumedSprintHours / this.sprintTotalHours) * 100;
        return Math.round(Math.max(progressPercentage, 0) * 100) / 100;
    }

    get taskProgressPercentage(): number {
        if (this.taskTotalHours <= 0) {
            return 0;
        }
        const progressPercentage = (this.consumedTaskHours / this.taskTotalHours) * 100;
        return Math.round(Math.max(progressPercentage, 0) * 100) / 100;
    }

    get meetingProgressPercentage(): number {
        if (this.meetingTotalHours <= 0) {
            return 0;
        }
        const progressPercentage = (this.consumedMeetingHours / this.meetingTotalHours) * 100;
        return Math.round(Math.max(progressPercentage, 0) * 100) / 100;
    }
}

export class ExtendedSprintProgress {
    color: string;
    iconName: string;
}

export class GetTodayFocusRequest {
    todaysDate: string;
}

export class TodayFocusData {
    toDoTaskList: TaskForTodayFocus[] = [];
    meetingResponseList: MeetingResponseForTodayFocus[] = [];
    delayedTaskList: TaskForTodayFocus[] = [];
    watchlistTaskList: TaskForTodayFocus[] = [];
    taskWithDependencies: TaskForTodayFocus[] = [];

    constructor(data: Partial<TodayFocusData>) {
        this.toDoTaskList = (data.toDoTaskList || []).map((item: any) => new TaskForTodayFocus(item));
        this.meetingResponseList = (data.meetingResponseList || []).map(
            (item: any) => new MeetingResponseForTodayFocus(item),
        );
        this.delayedTaskList = (data.delayedTaskList || []).map((item: any) => new TaskForTodayFocus(item));
        this.watchlistTaskList = (data.watchlistTaskList || []).map((item: any) => new TaskForTodayFocus(item));
        this.taskWithDependencies = (data.taskWithDependencies || []).map((item: any) => new TaskForTodayFocus(item));
    }
}

export class TaskForTodayFocus {
    taskIdentifier: number;
    taskNumber: string;
    taskTitle: string;
    taskDesc: string;
    taskTypeId: number;
    taskActStDate?: string;
    taskActEndDate?: string;
    taskExpStartDate: string;
    taskExpEndDate: string;
    newEffortDate?: string;
    taskWorkflowType?: string;
    workflowTaskStatusType?: string;
    currentActivityIndicator: number;
    currentlyScheduledTaskIndicator: boolean;
    taskProgressSystem: string;
    taskPriority: string;
    teamName?: string;
    fullName?: string;
    email?: string;
    createdDateTime: string;
    lastUpdatedDateTime: string;
    teamId: number;

    constructor(data: Partial<TaskForTodayFocus>) {
        Object.assign(this, data);
    }
}

export class MeetingResponseForTodayFocus {
    meetingNumber: number;
    meetingId: number;
    title: string;
    venue?: string;
    startDateTime?: string;
    agenda: string;

    constructor(data: Partial<MeetingResponseForTodayFocus>) {
        Object.assign(this, data);
    }
}

export class CapacityForTeam {
    message?: string;
    userCapacityDetails: UserCapacityDetails[];

    constructor(data: any) {
        this.message = data.message;
        this.userCapacityDetails = (data.userCapacityDetails || []).map((item: any) => new UserCapacityDetails(item));
    }
}

export class UserCapacityDetails {
    accountId: number;
    accountName: string;
    email: string;
    totalCapacity: number;
    currentPlannedCapacity: number;
    loadedCapacity: number;
    percentCapacityUtilization: number;
    percentLoadedCapacityUtilization: number;
    loadedCapacityRatio: number;
    totalWorkingDays?: number;
    workMinutes?: number;

    constructor(data: Partial<UserCapacityDetails>) {
        Object.assign(this, data);
    }
}

//Object and Constants to deal with locally data emitted for components

export class MyDashBoardBackendData {
    statsData: StatsApiResponse;
    teamMembers: UserAccount[];
}

export class ManagerViewBackendData {
    statsData: StatsApiResponse;
    teamMembers: UserAccount[];
}

export class SprintProgress {
    sprintId: number;
    totalSprintHours: number;
    consumedSprintHours: number;

    constructor(init: Partial<SprintProgress>) {
        Object.assign(this, init);
    }
}

export class TaskProgress {
    taskId: number;
    taskNumber: number;
    taskEstimate: number;
    earnedTimeTask: number;

    constructor(init: Partial<TaskProgress>) {
        Object.assign(this, init);
    }
}

export class MeetingProgress {
    meetingId: number;
    meetingDuration: number;
    attendeeDuration: number;

    constructor(init: Partial<MeetingProgress>) {
        Object.assign(this, init);
    }
}

export class ProgressReport {
    sprintProgress: SprintProgress[];
    sprintTotalHours: number;
    consumedSprintHours: number;
    taskProgress: TaskProgress[];
    taskTotalHours: number;
    consumedTaskHours: number;
    meetingProgress: MeetingProgress[];
    meetingTotalHours: number;
    consumedMeetingHours: number;

    constructor(init: Partial<ProgressReport>) {
        Object.assign(this, init);
    }
}

export class MemberOnLeaveCardToShow {
    name: string;
    date: string;
    entityName: string;
    accountId: number;
    leaveType: string;
}

export class AddMemberOnLeaveRequest {
    accountId: number;
    leaveType: string;
    fromDate: string;
    toDate: string;
}

export class AddUpdateReminderRequest {
    reminderTitle: string;
    description?: string;
    reminderDate: string;
    reminderTime: string;
    reminderStatus: ReminderStatus;
    isEarlyReminderSet: boolean = true;
    earlyReminderTime?: string | null = null;
    accountIdCreator: number;
}

class ReminderUserDetails {
    email: string;
    accountId: number;
    firstName: string;
    lastName: string;
}

export class GetAllReminderForUserResponse {
    todayReminders: ReminderResponse[];
    futureReminders: ReminderResponse[];
    pastReminders: ReminderResponse[];

    constructor(data: Partial<GetAllReminderForUserResponse>) {
        this.todayReminders = (data.todayReminders || []).map((item: any) => new ReminderResponse(item));
        this.futureReminders = (data.futureReminders || []).map((item: any) => new ReminderResponse(item));
        this.pastReminders = (data.pastReminders || []).map((item: any) => new ReminderResponse(item));
    }
}

export class ReminderResponse {
    reminderId: number;
    reminderTitle: string;
    description: string;
    reminderDate: string;
    reminderTime: string;
    earlyReminderTime: string | null = null;
    userDetails: ReminderUserDetails;
    reminderStatus: ReminderStatus;

    constructor(data: Partial<ReminderResponse>) {
        Object.assign(this, data);
    }

    get reminderDateTime(): Date | null {
        if (!this.reminderDate || !this.reminderTime) {
            return null;
        }

        return new Date(this.reminderDate+"T"+this.reminderTime)
    }

    get isCompleted(): boolean {
        return this.reminderStatus === ReminderStatus.COMPLETED;
    }
}

export class GetRemindersRequest {
    todaysDate: string;
}

class AlertUserDetails {
    email: string;
    accountId: number;
    firstName: string;
    lastName: string;
}

class AlertTeamDetails {
    teamId: number;
    teamName: string;
}

class AlertProjectDetails {
    projectId: number;
    projectName: string;
}

class AlertOrgDetails {
    orgId: number;
    organizationName: string;
}

export class Alert {
    alertId: number;
    alertTitle: string;
    alertReason: string;
    alertStatus: string;
    alertType: string;
    associatedTaskNumber: string;
    associatedTaskId: number;
    senderDetails: AlertUserDetails | null;
    receiverDetails: AlertUserDetails;
    teamDetails: AlertTeamDetails;
    projectDetails: AlertProjectDetails;
    orgDetails: AlertOrgDetails;
    createdDateTime: string;
}

export class DataNeededForDashboardFilters {
    viewMode: CustomDropdownOption;
    statRequest: StatsRequest;
    selectedEntityOnNavbar: EntityInfo | null;
}

export class SelectedDateRange {
    selectedFromDate: string;
    selectedToDate: string;
}

export type StatusNameClickEvent = {
    status: string;
    isTodayDate: boolean;
};

export const BarLabelConstants = {
    SPRINT: "Sprint",
    MEETINGS: "Meetings",
    OTHER_TASKS: "Other Tasks",
};

export class TodayFocusDetailedViewDialogData {
    data: MeetingResponseForTodayFocus[] | TaskForTodayFocus[];
    title: string;
    isTasks: boolean;
    titleTextColor: string;
}

export class DashBoardEntitySelectionEventResponse {
    data: EntityInfo | null;
}

export enum ReminderTabIndex{
    PAST = 0,
    TODAY = 1,
    FUTURE = 2,
}

export enum ReminderStatus {
    PENDING = "Pending",
    COMPLETED = "Completed",
    DELETED = "Deleted",
}
export class OrgTeamState {
    orgTeamStructure: OrgBUProjectTeamStructure[];
    isResetAllowed: boolean;
}

export const reminderOptions: CustomDropdownOption[] = [
    { value: null, label: 'None' },
    { value: 'atEvent', label: 'At time of event' },
    { value: '5m', label: '5 minutes before' },
    { value: '10m', label: '10 minutes before' },
    { value: '15m', label: '15 minutes before' },
    { value: '30m', label: '30 minutes before' },
    { value: '1h', label: '1 hour before' },
    { value: '1d', label: '1 day before' }
];

export class AiReminderDialogData {
    aiResponse: AiInsDetectionResponse;
    reminder: ReminderResponse | null;
}

export const AiReminderMinutesToOptionMap: Record<number, string> = {
    0: "atEvent",
    5: "5m",
    10: "10m",
    15: "15m",
    30: "30m",
    60: "1h",
    1440: "1d",
};

export const AiShowIdToReminderTabMap: Record<number, ReminderTabIndex> = {
    0: ReminderTabIndex.TODAY,
    1: ReminderTabIndex.PAST,
    2: ReminderTabIndex.FUTURE,
};